import type { BatchStatus } from '@gsls/shared';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui';
import { links } from '../../lib/links';
import { useSession } from '../../lib/session';
import { KpiStrip, type Kpi } from './primitives';
import { QuickActions, StickyQuickActions, type QuickAction } from './QuickActions';
import { BatchBoardWidget } from './widgets/BatchBoardWidget';
import { HoldQueueWidget } from './widgets/HoldQueueWidget';
import { LowStockWidget } from './widgets/LowStockWidget';
import { RecentRecoveriesWidget } from './widgets/RecentRecoveriesWidget';
import { lowStockQuery, onHoldQuery, openBatchesQuery, recoveriesThisMonthQuery } from './queries';

/**
 * The production floor. The next step for each batch is the headline: start it,
 * keep drawing, or close it — and anything running low is flagged before it
 * stops a batch mid-way.
 */
export function ProductionDashboard() {
  const { shows } = useSession();

  const board = useQuery(openBatchesQuery);
  const lowStock = useQuery(lowStockQuery);
  const hold = useQuery(onHoldQuery);
  const recoveries = useQuery(recoveriesThisMonthQuery());

  const batches = board.data?.items ?? [];
  const countBy = (status: BatchStatus) => batches.filter((batch) => batch.status === status).length;

  const drafts = countBy('draft');
  const inProgress = countBy('in_progress');
  const awaitingClose = countBy('completed');
  const recoveredOnHold = (hold.data?.items ?? []).filter((lot) => lot.origin === 'recovered')
    .length;
  const recoveredThisMonth = recoveries.data?.totals.recoveries;

  const kpis: Kpi[] = [
    { label: 'Drafts', value: board.isSuccess ? String(drafts) : '—', hint: 'waiting to start' },
    {
      label: 'In progress',
      value: board.isSuccess ? String(inProgress) : '—',
      tone: inProgress > 0 ? 'good' : 'default',
      hint: 'drawing material',
    },
    {
      label: 'Awaiting close',
      value: board.isSuccess ? String(awaitingClose) : '—',
      tone: awaitingClose > 0 ? 'warn' : 'default',
      hint: 'produced, not closed',
    },
    {
      label: 'Recovered this month',
      value: recoveredThisMonth === undefined ? '—' : String(recoveredThisMonth),
      hint: 'recoveries recorded',
    },
  ];

  const actions: QuickAction[] = [
    { label: 'New batch', to: links.newBatch(), capability: 'batch.manage', tone: 'primary' },
    { label: 'Record recovery', to: links.newRecovery(), capability: 'recovery.record' },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Production"
        title="Production floor"
        description="What each batch needs next, and the material that could stop one. Consumption draws stock down line by line, so on-hand is always current."
      />

      <KpiStrip items={kpis} />

      <div className="mt-4">
        <QuickActions actions={actions} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BatchBoardWidget to={shows('batches') ? '/batches' : undefined} />
        <LowStockWidget title="Materials low" to={shows('inventory') ? links.inventoryBalances() : undefined} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <HoldQueueWidget
          title="Recovered awaiting QC"
          origin="recovered"
          canDecide={false}
          to={shows('quality') ? '/quality' : undefined}
        />
        <RecentRecoveriesWidget to={shows('recovery') ? '/recovery' : undefined} />
      </div>

      <p className="mt-4 text-xs text-ink-muted">
        {recoveredOnHold > 0
          ? `${recoveredOnHold} recovered lot(s) are on hold and cannot be drawn until QC releases them.`
          : 'Recovered material that needs a QC pass will appear here.'}
      </p>

      <StickyQuickActions actions={actions} />
    </>
  );
}
